import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ModalContext from "@/hooks/useModalContext";
import { useOverlayDismiss } from "@/hooks/useOverlayDismiss";

// CSS for hiding WebKit scrollbars
const scrollbarHideStyle = `
.bottom-drawer-hide-scrollbar::-webkit-scrollbar {
  display: none;
}
`;

// Inject the CSS if not already injected
if (typeof document !== "undefined" && !document.getElementById("bottom-drawer-scrollbar-style")) {
  const style = document.createElement("style");
  style.id = "bottom-drawer-scrollbar-style";
  style.textContent = scrollbarHideStyle;
  document.head.appendChild(style);
}

export interface BottomDrawerProps {
  /** Shows or hides the drawer */
  isVisible: boolean;
  /** Callback when the drawer should be closed */
  onClose: () => void;
  /** If true, children won't be mounted until isVisible is true */
  mountOnEnter?: boolean;
  /** If true, children will be unmounted when isVisible becomes false */
  unmountOnExit?: boolean;
  /** Duration of the enter/exit animation in ms */
  duration?: number;
  /** If true, scrollbars won't appear even if content is scrollable */
  hideScrollbars?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for testing purposes */
  "data-testid"?: string;
}

export default function BottomDrawer({
  isVisible,
  onClose,
  mountOnEnter = true,
  unmountOnExit = true,
  duration = 250,
  hideScrollbars = false,
  className = "",
  "data-testid": testId,
  children
}: PropsWithChildren<BottomDrawerProps>) {
  const [isMounted, setIsMounted] = useState(!mountOnEnter || isVisible);
  const drawerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle mounting/unmounting logic
  useEffect(() => {
    if (isVisible && mountOnEnter && !isMounted) {
      setIsMounted(true);
    }
  }, [isVisible, mountOnEnter, isMounted]);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";

      if (contentRef.current) {
        const focusableElement = contentRef.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        focusableElement?.focus();
      }
    } else {
      document.body.style.overflow = "visible";

      if (unmountOnExit) {
        const timer = setTimeout(() => {
          setIsMounted(false);
        }, duration);
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, duration, unmountOnExit]);

  useOverlayDismiss({
    open: isVisible,
    dismiss: onClose
  });

  if (!isMounted) {
    return null;
  }

  const scrollableStyle = hideScrollbars
    ? {
        scrollbarWidth: "none" as const,
        msOverflowStyle: "none" as const
      }
    : {};

  const scrollableClasses = hideScrollbars
    ? "px-4 pb-4 max-h-[80vh] overflow-y-auto bottom-drawer-hide-scrollbar"
    : "px-4 pb-4 max-h-[80vh] overflow-y-auto";

  const handleBackdropClick = () => {
    onClose();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const drawerContent = (
    <div
      ref={drawerRef}
      className={`fixed inset-0 ${className}`}
      style={{
        zIndex: 9999,
        pointerEvents: isVisible ? "auto" : "none",
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${duration}ms ease-out`
      }}
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-hidden={!isVisible}
    >
      <div
        className="absolute inset-0 bg-sys-brand-scrim cursor-pointer"
        style={{
          opacity: isVisible ? 0.6 : 0,
          transition: `opacity ${duration}ms ease-out`
        }}
        onClick={handleBackdropClick}
      />

      <div
        ref={contentRef}
        className="absolute bottom-0 left-0 right-0 bg-sys-brand-surface rounded-t-2xl shadow-2xl"
        style={{
          transform: isVisible ? "translateY(0)" : "translateY(100%)",
          transition: `transform ${duration}ms ease-out`
        }}
        onClick={handleContentClick}
      >
        <div className="flex justify-center pt-2 pb-4">
          <div className="w-8 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className={scrollableClasses} style={scrollableStyle}>
          <ModalContext.Provider value={true}>{children}</ModalContext.Provider>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render outside component hierarchy
  return typeof document !== "undefined" ? createPortal(drawerContent, document.body) : null;
}
